import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import {
  HomeIcon,
  UserIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  NewspaperIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChartBarIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  CodeBracketIcon,
  WrenchScrewdriverIcon,
  PencilSquareIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin', icon: <HomeIcon className="h-5 w-5" /> },
    { name: 'Profile', path: '/admin/profile', icon: <UserIcon className="h-5 w-5" /> },
    { name: 'Projects', path: '/admin/projects', icon: <DocumentTextIcon className="h-5 w-5" /> },
    { name: 'Categories', path: '/admin/categories', icon: <TagIcon className="h-5 w-5" /> },
    { name: 'Experience', path: '/admin/experience', icon: <BriefcaseIcon className="h-5 w-5" /> },
    { name: 'Education', path: '/admin/education', icon: <AcademicCapIcon className="h-5 w-5" /> },
    { name: 'Publications', path: '/admin/publications', icon: <NewspaperIcon className="h-5 w-5" /> },
    // Open Source Contributions Section
    { name: 'Pub Packages', path: '/admin/pub-packages', icon: <CodeBracketIcon className="h-5 w-5" /> },
    { name: 'Dev Tools', path: '/admin/starter-templates', icon: <WrenchScrewdriverIcon className="h-5 w-5" /> },
    { name: 'Blogs', path: '/admin/blogs', icon: <PencilSquareIcon className="h-5 w-5" /> },
    { name: 'Messages', path: '/admin/messages', icon: <ChatBubbleOvalLeftEllipsisIcon className="h-5 w-5" /> },
    { name: 'Analytics', path: '/admin/analytics', icon: <ChartBarIcon className="h-5 w-5" /> },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 dark:bg-gray-900">
      {/* Sidebar for desktop */}
      <aside
        className={`${
          isSidebarOpen ? 'block' : 'hidden'
        } fixed inset-y-0 z-50 flex-shrink-0 w-64 md:block overflow-y-auto bg-white dark:bg-gray-800 shadow-md md:relative`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-4 border-b dark:border-gray-700">
            <span className="text-lg font-bold text-primary-600 dark:text-primary-400">Admin Dashboard</span>
            <button
              className="p-1 rounded-md text-gray-500 md:hidden"
              onClick={() => setIsSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 px-2 py-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
                  (location.pathname === item.path || 
                  (item.path === '/admin' && location.pathname === '/admin/'))
                    ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {item.icon}
                <span className="ml-3">{item.name}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t dark:border-gray-700">
            <button
              onClick={handleSignOut}
              className="flex items-center w-full px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
              <span className="ml-3">Sign Out</span>
            </button>
            <Link
              to="/"
              className="flex items-center w-full px-4 py-2 mt-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            >
              <span className="ml-3">Back to Site</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar toggle */}
      <div className="fixed top-0 left-0 right-0 z-40 flex h-16 md:hidden items-center bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4">
        <button
          className="p-1 text-gray-500"
          onClick={() => setIsSidebarOpen(true)}
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <span className="ml-4 text-lg font-bold text-primary-600 dark:text-primary-400">Admin Dashboard</span>
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto pt-16 md:pt-0">
          <div className="py-6 px-4 md:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 